import { BASE_URL } from '../constants';
import { apiFetch } from '../ApiFetch';
import { getUserSessionCookie } from '../../utils/SessionCookie';
import type { UserConfirmedSelections } from '../../types/hubspot';
import {
  MappingConfig,
  SalesforceBrowseRecord,
  SalesforceFieldForMapping,
  SalesforceTargetObjectName,
  UserConfirmedLookups
} from '../../types/salesforce';

export interface SalesforceListResponse<T> {
  records: T[];
  /** Next list request: `GET ...?offset=<offsetId>` (from `paging.next.offset`). */
  offsetId?: string | null;
}

const authHeaders = (): Record<string, string> => {
  const sessionCookie = getUserSessionCookie();
  return {
    'Content-Type': 'application/json',
    ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
  };
};

/**
 * List API pagination: `{ paging: { has_next_page, next: { offset } } }`.
 */
export function parseSalesforceListPaging(json: Record<string, any>): string | null {
  const { paging } = json;
  if (!paging?.has_next_page) return null;
  const offset = paging.next?.offset;
  return offset !== undefined && offset !== null ? String(offset) : null;
}

/** Flat Salesforce rows (`Id`, `Name`, …) → `{ id, properties }` for Shared UI with HubSpot. */
export function normalizeSalesforceBrowseRecord(
  raw: Record<string, any>
): SalesforceBrowseRecord {
  const id = String(raw.Id ?? '');
  return { id, properties: { ...raw } };
}

export async function fetchSalesforceAccountProperties(): Promise<{
  properties: Record<string, string>;
}> {
  const res = await apiFetch(
    `${BASE_URL}/connector/salesforce/accounts/properties`,
    {
      method: 'GET',
      credentials: 'include',
      headers: authHeaders()
    }
  );
  if (!res.ok)
    throw new Error(
      `Fetch Salesforce account properties failed: ${res.status}`
    );
  return res.json();
}

export async function fetchSalesforceContactProperties(): Promise<{
  properties: Record<string, string>;
}> {
  const res = await apiFetch(
    `${BASE_URL}/connector/salesforce/contacts/properties`,
    {
      method: 'GET',
      credentials: 'include',
      headers: authHeaders()
    }
  );
  if (!res.ok)
    throw new Error(
      `Fetch Salesforce contact properties failed: ${res.status}`
    );
  return res.json();
}

export const listSalesforceAccounts = async (args: {
  limit?: number;
  offsetId?: string | null;
  search?: string | null;
  filter_by_my_owner?: boolean;
  properties?: string[];
}): Promise<SalesforceListResponse<SalesforceBrowseRecord>> => {
  const params = new URLSearchParams();
  if (args.limit != null) params.set('limit', String(args.limit));
  if (args.offsetId != null && args.offsetId !== '')
    params.set('offset', args.offsetId);
  if (args.search) params.set('search', args.search);
  if (args.filter_by_my_owner !== undefined)
    params.set('filter_by_my_owner', String(args.filter_by_my_owner));
  if (args.properties?.length)
    params.set('properties', args.properties.join(','));

  const res = await apiFetch(
    `${BASE_URL}/connector/salesforce/accounts/list?${params.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: authHeaders()
    }
  );
  if (!res.ok)
    throw new Error(`List Salesforce accounts failed: ${res.status}`);

  const json = await res.json();
  const results = json.results as Record<string, any>[];
  return {
    records: results.map(normalizeSalesforceBrowseRecord),
    offsetId: parseSalesforceListPaging(json)
  };
};

export const listSalesforceContacts = async (args: {
  limit?: number;
  offsetId?: string | null;
  search?: string | null;
  properties?: string[];
}): Promise<SalesforceListResponse<SalesforceBrowseRecord>> => {
  const params = new URLSearchParams();
  if (args.limit != null) params.set('limit', String(args.limit));
  if (args.offsetId != null && args.offsetId !== '')
    params.set('offset', args.offsetId);
  if (args.search) params.set('search', args.search);
  if (args.properties?.length)
    params.set('properties', args.properties.join(','));

  const res = await apiFetch(
    `${BASE_URL}/connector/salesforce/contacts/list?${params.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: authHeaders()
    }
  );
  if (!res.ok)
    throw new Error(`List Salesforce contacts failed: ${res.status}`);

  const json = await res.json();
  const results = json.results as Record<string, any>[];
  return {
    records: results.map(normalizeSalesforceBrowseRecord),
    offsetId: parseSalesforceListPaging(json)
  };
};

export async function fetchSalesforceAccountsByIds(args: {
  accountIds: string[];
  properties?: string[];
}): Promise<SalesforceBrowseRecord[]> {
  const params = new URLSearchParams();
  args.accountIds.forEach((id) => params.append('account_ids', id));
  if (args.properties?.length) {
    params.set('properties', args.properties.join(','));
  }

  const res = await apiFetch(
    `${BASE_URL}/connector/salesforce/accounts?${params.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: authHeaders()
    }
  );
  if (!res.ok)
    throw new Error(`Fetch Salesforce accounts by ids failed: ${res.status}`);

  const json = await res.json();
  return (json.results as Record<string, any>[]).map(
    normalizeSalesforceBrowseRecord
  );
}

export async function fetchSalesforceContactsByIds(args: {
  contactIds: string[];
  properties?: string[];
}): Promise<SalesforceBrowseRecord[]> {
  const params = new URLSearchParams();
  args.contactIds.forEach((id) => params.append('contact_ids', id));
  if (args.properties?.length) {
    params.set('properties', args.properties.join(','));
  }

  const res = await apiFetch(
    `${BASE_URL}/connector/salesforce/contacts?${params.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: authHeaders()
    }
  );
  if (!res.ok)
    throw new Error(`Fetch Salesforce contacts by ids failed: ${res.status}`);

  const json = await res.json();
  return (json.results as Record<string, any>[]).map(
    normalizeSalesforceBrowseRecord
  );
}

export const listSalesforceReports = async (args: {
  limit?: number;
  nextUrl?: string | null;
}) => {
  const sessionCookie = getUserSessionCookie();
  const params = new URLSearchParams();
  if (args.limit) params.set('limit', String(args.limit));
  if (args.nextUrl) params.set('next_url', args.nextUrl);

  const res = await apiFetch(
    `${BASE_URL}/connector/salesforce/reports/list?${params.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
      }
    }
  );
  if (!res.ok) throw new Error(`List reports failed: ${res.status}`);

  const json = await res.json();
  return {
    records: (json.records || []).map((r: any) => ({
      id: r.id,
      name: r.name,
      lastRunDate: r.last_run_date ?? null
    })),
    nextUrl: json.next_url ?? null
  };
};

export const getSalesforceReportRows = async (args: {
  reportId: string;
  limit?: number; // default 500 on BE
}) => {
  const sessionCookie = getUserSessionCookie();
  const params = new URLSearchParams();
  if (args.limit) params.set('limit', String(args.limit));

  const res = await apiFetch(
    `${BASE_URL}/connector/salesforce/reports/${args.reportId}/rows?${params.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
      }
    }
  );
  if (!res.ok) throw new Error(`Get report rows failed: ${res.status}`);

  const json = await res.json();
  return {
    reportId: json.report_id ?? json.reportId ?? args.reportId,
    table: json.table // { columns: [{key,label}], rows: any[][], rowCount: number }
  };
};

export async function getSalesforceSchemaForMapping(
  sobject: SalesforceTargetObjectName
): Promise<{
  sobject: SalesforceTargetObjectName;
  fields: SalesforceFieldForMapping[];
}> {
  const sessionCookie = getUserSessionCookie();
  const res = await apiFetch(
    `${BASE_URL}/connector/salesforce/schema/${sobject}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
      }
    }
  );
  if (!res.ok) throw new Error(`Describe failed: ${res.status}`);
  return res.json();
}

export async function startSalesforceMappedUpsertJob(args: {
  mapping: MappingConfig;
  rows: Record<string, any>[];
  /** Normalized primary key → Salesforce record Id to update, or null for create-not_found rows. */
  user_confirmed_selections?: UserConfirmedSelections | null;
  user_confirmed_lookups?: UserConfirmedLookups | null;
}): Promise<{ job_id: string; status: string }> {
  const sessionCookie = getUserSessionCookie();
  const res = await apiFetch(
    `${BASE_URL}/connector/salesforce/mapped-upsert/start`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
      },
      body: JSON.stringify(args)
    }
  );
  if (!res.ok) throw new Error(`Start job failed: ${res.status}`);
  return res.json();
}

/**
 * Get status for a Salesforce mapped upsert job (works for both preview and upsert jobs).
 */
export async function getSalesforceMappedUpsertJobStatus(jobId: string) {
  const sessionCookie = getUserSessionCookie();
  const res = await apiFetch(
    `${BASE_URL}/connector/salesforce/mapped-upsert/status?job_id=${encodeURIComponent(jobId)}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
      }
    }
  );
  if (!res.ok) throw new Error(`Status failed: ${res.status}`);
  return res.json();
}

/**
 * Get result for a Salesforce mapped upsert job (works for both preview and upsert jobs).
 */
export async function getSalesforceMappedUpsertJobResult(jobId: string) {
  const sessionCookie = getUserSessionCookie();
  const res = await apiFetch(
    `${BASE_URL}/connector/salesforce/mapped-upsert/result?job_id=${encodeURIComponent(jobId)}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
      }
    }
  );
  if (!res.ok) throw new Error(`Result failed: ${res.status}`);
  return res.json();
}

export async function previewSalesforceMappedUpsert(args: {
  mapping: MappingConfig;
  rows: Record<string, any>[];
}): Promise<{
  job_id: string;
  status: string;
  created_at: string;
  queue_position?: number;
}> {
  const sessionCookie = getUserSessionCookie();
  const res = await apiFetch(
    `${BASE_URL}/connector/salesforce/mapped-upsert/preview`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
      },
      body: JSON.stringify(args)
    }
  );
  if (!res.ok) {
    let message = `Preview failed (${res.status})`;
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      try {
        const data = (await res.json()) as { detail?: unknown };
        if (typeof data.detail === 'string' && data.detail.trim() !== '') {
          message = data.detail;
        }
      } catch {
        // keep generic message
      }
    }
    throw new Error(message);
  }
  return res.json();
}
